import type { CustomerSupplierOption } from '@invoice/shared/schemas';
import { Loader2, Search, X } from 'lucide-react';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCustomerSupplierList } from '@/lib/queries/customers';
import { cn } from '@/lib/utils';

interface SupplierLookupFieldProps {
  customerId?: string | null | undefined;
  allowGlobalSearch?: boolean | undefined;
  label: string;
  inputId?: string | undefined;
  placeholder?: string | undefined;
  idleHint?: string | undefined;
  loadingText?: string | undefined;
  emptyText?: string | undefined;
  selectedPrefix?: string | undefined;
  clearAriaLabel?: string | undefined;
  selectedSupplierId?: string | null | undefined;
  selectedSupplierName?: string | null | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  errorMessage?: string | undefined;
  inputTestId?: string | undefined;
  resultsTestId?: string | undefined;
  onSelect: (supplier: CustomerSupplierOption | null) => void;
}

export function SupplierLookupField({
  customerId,
  allowGlobalSearch = false,
  label,
  inputId,
  placeholder = 'Lieferantenname suchen',
  idleHint = 'Mindestens 2 Zeichen eingeben, um Lieferanten zu suchen.',
  loadingText = 'Lieferanten werden gesucht...',
  emptyText = 'Keine Lieferanten gefunden.',
  selectedPrefix = 'Ausgewählt',
  clearAriaLabel,
  selectedSupplierId,
  selectedSupplierName,
  disabled = false,
  required = false,
  errorMessage,
  inputTestId,
  resultsTestId,
  onSelect,
}: SupplierLookupFieldProps) {
  const [search, setSearch] = useState(selectedSupplierName ?? '');
  const [searchDirty, setSearchDirty] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (selectedSupplierId && selectedSupplierName) {
      setSearch(selectedSupplierName);
      setSearchDirty(false);
      return;
    }

    if (!selectedSupplierId && !selectedSupplierName && !searchDirty) {
      setSearch('');
    }
  }, [searchDirty, selectedSupplierId, selectedSupplierName]);

  const normalizedSearch = search.trim();
  const hasSearchScope =
    allowGlobalSearch || (typeof customerId === 'string' && customerId.length > 0);
  const shouldSearch = !disabled && hasSearchScope && focused;
  const querySearch = searchDirty && normalizedSearch.length > 0 ? normalizedSearch : undefined;

  const suppliersQuery = useCustomerSupplierList({
    customerId,
    search: shouldSearch ? querySearch : undefined,
    enabled: shouldSearch,
  });

  const results = suppliersQuery.data?.items ?? [];
  const resultsVisible = shouldSearch;
  const selectedSummary = useMemo(() => {
    const visibleName =
      selectedSupplierName && selectedSupplierName.length > 0 ? selectedSupplierName : search;
    if (visibleName.length === 0) return null;
    if (selectedSupplierId) {
      return `${visibleName} (${selectedSupplierId})`;
    }
    return visibleName;
  }, [search, selectedSupplierId, selectedSupplierName]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setSearch(nextValue);
    setSearchDirty(true);

    if (selectedSupplierId || selectedSupplierName) {
      onSelect(null);
    }
  };

  const handleSelect = (supplier: CustomerSupplierOption) => {
    setSearch(supplier.name);
    setSearchDirty(false);
    onSelect(supplier);
  };

  const handleClear = () => {
    setSearch('');
    setSearchDirty(false);
    onSelect(null);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId}>
        {label}
        {required ? ' *' : ''}
      </Label>

      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={inputId}
          value={search}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          aria-invalid={errorMessage ? true : undefined}
          aria-label={label}
          disabled={disabled || !hasSearchScope}
          data-testid={inputTestId}
          className="pl-9 pr-11"
        />
        {search.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            onMouseDown={(event) => event.preventDefault()}
            disabled={disabled || !hasSearchScope}
            aria-label={clearAriaLabel ?? `${label} leeren`}
            className="absolute right-1 top-1/2 size-8 -translate-y-1/2"
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      {selectedSummary && !searchDirty ? (
        <p className="text-xs text-muted-foreground">
          {selectedPrefix}: {selectedSummary}
        </p>
      ) : null}

      {resultsVisible ? (
        <div
          className={cn(
            'rounded-md border border-border bg-card',
            suppliersQuery.isPending ? 'p-3' : 'max-h-56 overflow-y-auto',
          )}
          data-testid={resultsTestId}
        >
          {suppliersQuery.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {loadingText}
            </div>
          ) : results.length > 0 ? (
            <ul className="divide-y divide-border">
              {results.map((supplier) => (
                <li key={`${supplier.kind}:${supplier.id}`}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(supplier)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-muted"
                    data-testid={`supplier-lookup-option-${supplier.id}`}
                  >
                    <span className="font-medium text-foreground">{supplier.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {supplier.id}
                      {supplier.kind === 'observed' ? ' · beobachtet' : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-3 text-sm text-muted-foreground">{emptyText}</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{idleHint}</p>
      )}

      {errorMessage ? (
        <p className="text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
